import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { checkImageQuality } from "@/lib/imageQuality";
import { trackConversion } from "@/lib/weeklySpecialTracking";
import { Loader2, Upload, Check, IdCard, Camera, ShieldCheck, FileSignature, CreditCard, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errMsg = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(", ");
  if (typeof d === "string") return d;
  if (d && typeof d === "object") return d.msg || fallback;
  return fallback;
};

const STEP_META = [
  { key: "docs", label: "Documents", icon: IdCard },
  { key: "info", label: "Your Info", icon: ShieldCheck },
  { key: "sign", label: "Sign", icon: FileSignature },
  { key: "card", label: "Card on File", icon: CreditCard },
];

function UploadTile({ label, icon: Icon, value, onUpload, testid }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    checkImageQuality(f).then((q) => q.warnings.forEach((w) => toast.warning(w, { duration: 6000 })));
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpload(data.path);
      toast.success(`${label} uploaded`);
    } catch { toast.error(`Could not upload ${label}`); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <label data-testid={testid} className={`flex flex-col items-center justify-center gap-2 h-32 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${value ? "border-green-500/50 bg-green-500/10 text-green-400" : "border-white/15 hover:border-[#F2B705]/60 text-white/60"}`}>
      {busy ? <Loader2 className="animate-spin" size={22} /> : value ? <Check size={26} /> : <Icon size={22} />}
      <span className="text-sm font-semibold">{value ? `${label} ✓` : label}</span>
      <span className="text-[11px] text-white/35">{value ? "Tap to replace" : "Tap to upload"}</span>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
    </label>
  );
}

export default function CompleteBooking() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("docs");
  const [docs, setDocs] = useState({ license_front_path: "", license_back_path: "", selfie_path: "", insurance_card_path: "" });
  const [info, setInfo] = useState({ date_of_birth: "", address: "", dl_number: "", dl_state: "", dl_expiration: "" });
  const [busy, setBusy] = useState(false);

  const computeStep = (d) => {
    const docsDone = d.has_license && d.has_selfie && (!d.needs_insurance || d.has_insurance);
    if (!docsDone) return "docs";
    if (!d.info_done) return "info";
    if (!d.signed) return "sign";
    if (!d.card_saved) return "card";
    return "done";
  };

  const fetchData = async (autoStep = true) => {
    try {
      const { data: d } = await axios.get(`${API}/complete/${token}`);
      setData(d);
      setInfo((i) => ({ ...i, date_of_birth: i.date_of_birth || d.date_of_birth, address: i.address || d.address, dl_number: i.dl_number || d.dl_number, dl_state: i.dl_state || d.dl_state, dl_expiration: i.dl_expiration || d.dl_expiration }));
      setDocs((p) => ({ license_front_path: d.has_license ? "on-file" : p.license_front_path, license_back_path: p.license_back_path, selfie_path: d.has_selfie ? "on-file" : p.selfie_path, insurance_card_path: d.has_insurance ? "on-file" : p.insurance_card_path }));
      if (autoStep) setStep(computeStep(d));
      return d;
    } catch (e) {
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    trackConversion("SubmitApplication");
    // returning from Stripe card setup
    const ss = sp.get("setup_session");
    if (ss) {
      axios.get(`${API}/payments/setup-status/${ss}`).then(() => { toast.success("Card saved!"); fetchData(); }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [token]);

  const submitDocs = async () => {
    setBusy(true);
    try {
      const payload = {};
      ["license_front_path", "license_back_path", "selfie_path", "insurance_card_path"].forEach((k) => { if (docs[k] && docs[k] !== "on-file") payload[k] = docs[k]; });
      if (Object.keys(payload).length) await axios.post(`${API}/complete/${token}/info`, payload);
      const d = await fetchData(false);
      const docsDone = d.has_license && d.has_selfie && (!d.needs_insurance || d.has_insurance);
      if (!docsDone) { toast.error("Please upload your license and selfie" + (d.needs_insurance ? " and insurance card" : "")); }
      else setStep("info");
    } catch { toast.error("Could not save documents"); }
    finally { setBusy(false); }
  };

  const submitInfo = async () => {
    if (!info.date_of_birth || !info.address) { toast.error("Date of birth and address are required"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/complete/${token}/info`, info);
      await fetchData(false);
      setStep("sign");
    } catch { toast.error("Could not save your info"); }
    finally { setBusy(false); }
  };

  const goSign = () => {
    if (!data.agreement_sign_token) {
      toast.info("Preparing your agreement…");
      fetchData();
      return;
    }
    window.location.href = `/sign/${data.agreement_sign_token}?return=${encodeURIComponent(`/complete/${token}`)}`;
  };

  const saveCard = async () => {
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/payments/setup-card`, { booking_id: data.booking_id, origin_url: window.location.origin, return_path: `/complete/${token}` });
      window.location.href = r.url;
    } catch (e) { toast.error(errMsg(e, "Could not start card setup")); setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#050505]"><Loader2 className="animate-spin text-[#F2B705]" size={32} /></div>;
  if (!data) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#050505] text-center px-6">
      <FileSignature className="text-white/30 mb-4" size={40} />
      <h1 className="text-2xl font-bold text-white">Link invalid or expired</h1>
      <p className="text-white/50 mt-2">Please contact Auto-Hire STL for a new link.</p>
    </div>
  );

  const stepIdx = STEP_META.findIndex((s) => s.key === step);

  return (
    <div className="min-h-screen bg-[#050505] text-white pb-16" data-testid="complete-booking">
      <div className="max-w-2xl mx-auto px-5 pt-8">
        <h1 className="font-display font-black text-3xl">Complete Your Booking</h1>
        <p className="text-white/50 mt-1">{data.vehicle} · {data.pickup_date} → {data.return_date}</p>

        {/* progress */}
        <div className="flex items-center justify-between mt-6 mb-8">
          {STEP_META.map((s, i) => {
            const Icon = s.icon;
            const done = step === "done" || i < stepIdx;
            const active = i === stepIdx;
            return (
              <div key={s.key} className="flex-1 flex flex-col items-center gap-1.5">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${done ? "bg-green-500 border-green-500 text-black" : active ? "bg-[#F2B705] border-[#F2B705] text-black" : "border-white/20 text-white/40"}`}>
                  {done ? <Check size={18} /> : <Icon size={17} />}
                </div>
                <span className={`text-[11px] ${active ? "text-[#F2B705]" : "text-white/40"}`}>{s.label}</span>
              </div>
            );
          })}
        </div>

        {step === "docs" && (
          <div className="space-y-4" data-testid="step-docs">
            {data.reverify && (
              <div data-testid="reverify-banner" className="rounded-lg border border-amber-400/40 bg-amber-400/[0.08] text-amber-300 text-sm px-4 py-3">
                We need updated verification documents. Please re-upload a clear photo of your <b>license (front &amp; back)</b> and a new <b>selfie</b>.
              </div>
            )}
            <p className="text-white/70 text-sm">Upload clear photos of your documents.</p>
            <div className="grid grid-cols-2 gap-3">
              <UploadTile label="License Front" icon={IdCard} value={docs.license_front_path} onUpload={(p) => setDocs({ ...docs, license_front_path: p })} testid="upload-license-front" />
              <UploadTile label="License Back" icon={IdCard} value={docs.license_back_path} onUpload={(p) => setDocs({ ...docs, license_back_path: p })} testid="upload-license-back" />
              <UploadTile label="Selfie" icon={Camera} value={docs.selfie_path} onUpload={(p) => setDocs({ ...docs, selfie_path: p })} testid="upload-selfie" />
              {data.needs_insurance && <UploadTile label="Insurance Card" icon={ShieldCheck} value={docs.insurance_card_path} onUpload={(p) => setDocs({ ...docs, insurance_card_path: p })} testid="upload-insurance" />}
            </div>
            <button data-testid="docs-continue" onClick={submitDocs} disabled={busy} className="w-full py-3 rounded-xl bg-[#F2B705] text-black font-bold disabled:opacity-60">
              {busy ? "Saving…" : "Continue"}
            </button>
          </div>
        )}

        {step === "info" && (
          <div className="space-y-3" data-testid="step-info">
            <p className="text-white/70 text-sm">Confirm your details.</p>
            <div>
              <label className="text-xs text-white/50">Date of Birth</label>
              <input data-testid="info-dob" type="date" value={info.date_of_birth} onChange={(e) => setInfo({ ...info, date_of_birth: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg bg-[#0C0C0C] border border-white/15 text-white outline-none focus:border-[#F2B705]" />
            </div>
            <div>
              <label className="text-xs text-white/50">Home Address</label>
              <input data-testid="info-address" value={info.address} onChange={(e) => setInfo({ ...info, address: e.target.value })} placeholder="Street, City, State, ZIP" className="mt-1 w-full px-3 py-2.5 rounded-lg bg-[#0C0C0C] border border-white/15 text-white outline-none focus:border-[#F2B705]" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-xs text-white/50">DL Number</label><input data-testid="info-dl" value={info.dl_number} onChange={(e) => setInfo({ ...info, dl_number: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg bg-[#0C0C0C] border border-white/15 text-white outline-none focus:border-[#F2B705]" /></div>
              <div><label className="text-xs text-white/50">State</label><input value={info.dl_state} onChange={(e) => setInfo({ ...info, dl_state: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg bg-[#0C0C0C] border border-white/15 text-white outline-none focus:border-[#F2B705]" /></div>
              <div><label className="text-xs text-white/50">DL Expires</label><input type="date" value={info.dl_expiration} onChange={(e) => setInfo({ ...info, dl_expiration: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg bg-[#0C0C0C] border border-white/15 text-white outline-none focus:border-[#F2B705]" /></div>
            </div>
            <button data-testid="info-continue" onClick={submitInfo} disabled={busy} className="w-full py-3 rounded-xl bg-[#F2B705] text-black font-bold disabled:opacity-60">{busy ? "Saving…" : "Continue"}</button>
          </div>
        )}

        {step === "sign" && (
          <div className="space-y-4 text-center" data-testid="step-sign">
            <FileSignature className="mx-auto text-[#F2B705]" size={40} />
            <h2 className="font-display font-bold text-xl">Your Rental Agreement Is Ready</h2>
            <p className="text-white/60 text-sm">
              You'll review the full agreement, initial the coverage sections, and type the acknowledgment
              statement exactly as shown before signing electronically.
            </p>
            <button data-testid="go-sign-agreement" onClick={goSign}
              className="w-full py-3 rounded-xl bg-[#F2B705] text-black font-bold hover:bg-white transition-colors">
              Review &amp; Sign Agreement
            </button>
            {!data.agreement_sign_token && (
              <p className="text-white/40 text-xs">Your agreement is being prepared — tap the button to refresh.</p>
            )}
          </div>
        )}

        {step === "card" && (
          <div className="space-y-4 text-center" data-testid="step-card">
            <CreditCard className="mx-auto text-[#F2B705]" size={40} />
            <p className="text-white/70 text-sm">Last step — securely save a card on file (held by Stripe). You won't be charged now.</p>
            <button data-testid="card-save" onClick={saveCard} disabled={busy} className="w-full py-3 rounded-xl bg-[#F2B705] text-black font-bold disabled:opacity-60">{busy ? "Redirecting…" : "Save Card Securely"}</button>
          </div>
        )}

        {step === "done" && (
          <div className="text-center py-10" data-testid="step-done">
            <CheckCircle2 className="mx-auto text-green-500" size={56} />
            <h2 className="font-display font-black text-2xl mt-4">You're all set!</h2>
            <p className="text-white/60 mt-2">Your booking is complete. We'll see you at pickup — Ride With Us!</p>
            {data.card && <p className="text-white/40 text-sm mt-3">Card on file: {data.card.brand} •••• {data.card.last4}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
